import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ClipboardCheck,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Shield,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Download,
} from '@/lib/icons';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { cn } from '@/lib/utils';
import { saveBlob } from '@/lib/download';
import { getAuditLogs } from '@/features/audit-logs/api';
import type { AuditLogEntry } from '@/features/audit-logs/types';
import { businessToday, formatDateTime, isToday, toInstant } from '@/lib/dates';
import { TimeZoneHint } from '@/components/common/time-zone-hint';

export const Route = createFileRoute('/_layout/audit-logs/')({
  component: AuditLogsPage,
});

/* ── Helpers ──────────────────────────────────────────── */

const resultBadge = (result: string) => {
  const isSuccess = result.toLowerCase().includes('success');
  return (
    <Badge
      className={cn(
        'gap-1 text-xs',
        isSuccess
          ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400'
          : 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400'
      )}
    >
      {isSuccess ? (
        <CheckCircle2 className="w-3 h-3" />
      ) : (
        <XCircle className="w-3 h-3" />
      )}
      {result}
    </Badge>
  );
};

const roleBadge = (role: string) => {
  const colors: Record<string, string> = {
    Agent:
      'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
    'Branch Manager':
      'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
    'Tenant Admin':
      'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400',
    System:
      'bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400',
  };
  return (
    <Badge className={cn('text-xs', colors[role] || colors.System)}>
      {role}
    </Badge>
  );
};

const channelBadge = (channel: string) => {
  const colors: Record<string, string> = {
    'ZAAD API':
      'bg-teal-500/15 text-teal-600 border-teal-500/30 dark:text-teal-400',
    'Agent App':
      'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
    'Branch Counter':
      'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
  };
  return (
    <Badge
      variant="outline"
      className={cn('text-[10px]', colors[channel] || '')}
    >
      {channel}
    </Badge>
  );
};

/* ── Expanded Row Detail ──────────────────────────────── */

function AuditDetail({ entry }: { entry: AuditLogEntry }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg text-sm animate-fade-in">
      <div>
        <p className="text-xs text-muted-foreground">Action ID</p>
        <p className="font-mono text-xs mt-0.5">{entry.actionId}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Branch</p>
        <p className="mt-0.5">{entry.branch || '—'}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Customer MSISDN</p>
        <p className="font-mono text-xs mt-0.5">
          {entry.customerMsisdn || '—'}
        </p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Contract ID</p>
        <p className="font-mono text-xs mt-0.5 text-primary">
          {entry.contractId || '—'}
        </p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Device ID / IMEI</p>
        <p className="font-mono text-xs mt-0.5">{entry.deviceId || '—'}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Old Value</p>
        <p className="mt-0.5">
          {entry.oldValue ? (
            <span className="text-red-500 line-through">{entry.oldValue}</span>
          ) : (
            '—'
          )}
        </p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">New Value</p>
        <p className="mt-0.5">
          {entry.newValue ? (
            <span className="text-emerald-600 font-medium">
              {entry.newValue}
            </span>
          ) : (
            '—'
          )}
        </p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Approval Reference</p>
        <p className="font-mono text-xs mt-0.5">
          {entry.approvalReference || '—'}
        </p>
      </div>
      <div className="col-span-2 md:col-span-4">
        <p className="text-xs text-muted-foreground">Reason</p>
        <p className="mt-0.5">{entry.reason || '—'}</p>
      </div>
    </div>
  );
}

/* ── Filter Options ───────────────────────────────────── */

const roleOptions = [
  { value: 'all', label: 'All Roles' },
  { value: 'Agent', label: 'Agent' },
  { value: 'Branch Manager', label: 'Branch Manager' },
  { value: 'Tenant Admin', label: 'Tenant Admin' },
  { value: 'System', label: 'System' },
];

const actionOptions = [
  { value: 'all', label: 'All Actions' },
  { value: 'Lock', label: 'Lock' },
  { value: 'Unlock', label: 'Unlock' },
  { value: 'Cash Payment', label: 'Cash Payment' },
  { value: 'Write Off', label: 'Write Off' },
  { value: 'Contract Activated', label: 'Contract Activated' },
  { value: 'Device Activated', label: 'Device Activated' },
  { value: 'Payment Received', label: 'Payment Received' },
  { value: 'KYC Verified', label: 'KYC Verified' },
  { value: 'Manual Adjustment', label: 'Manual Adjustment' },
];

const resultOptions = [
  { value: 'all', label: 'All Results' },
  { value: 'Success', label: 'Success' },
  { value: 'Failed', label: 'Failed' },
];

const channelOptions = [
  { value: 'all', label: 'All Channels' },
  { value: 'ZAAD API', label: 'ZAAD API' },
  { value: 'Agent App', label: 'Agent App' },
  { value: 'Branch Counter', label: 'Branch Counter' },
  { value: 'Admin Portal', label: 'Admin Portal' },
  { value: 'System', label: 'System' },
];

/* ── Main Page ────────────────────────────────────────── */

function AuditLogsPage() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: () => getAuditLogs(),
    retry: 1,
    staleTime: 30_000,
  });

  const allLogs = useMemo(() => data?.value ?? [], [data]);

  const filtered = useMemo(() => {
    return allLogs.filter((log) => {
      const matchSearch =
        !search ||
        log.actionId.toLowerCase().includes(search.toLowerCase()) ||
        log.performedBy.toLowerCase().includes(search.toLowerCase()) ||
        log.actionName.toLowerCase().includes(search.toLowerCase()) ||
        log.contractId?.toLowerCase().includes(search.toLowerCase()) ||
        log.customerMsisdn?.includes(search) ||
        log.deviceId?.toLowerCase().includes(search.toLowerCase());
      const matchRole = roleFilter === 'all' || log.userRole === roleFilter;
      const matchAction =
        actionFilter === 'all' || log.actionName === actionFilter;
      const matchResult =
        resultFilter === 'all' || log.result === resultFilter;
      const matchChannel =
        channelFilter === 'all' || log.sourceChannel === channelFilter;
      return (
        matchSearch && matchRole && matchAction && matchResult && matchChannel
      );
    });
  }, [allLogs, search, roleFilter, actionFilter, resultFilter, channelFilter]);

  // Compute stats
  const totalEvents = allLogs.length;
  const failedActions = allLogs.filter((l) =>
    l.result.toLowerCase().includes('fail')
  ).length;
  const manualApprovals = allLogs.filter(
    (l) => l.approvalReference != null && l.approvalReference !== ''
  ).length;
  const todayActions = allLogs.filter((l) => isToday(l.timestamp)).length;

  const stats = [
    {
      label: 'Total Events',
      value: totalEvents,
      icon: ClipboardCheck,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: 'Failed Actions',
      value: failedActions,
      icon: XCircle,
      color: 'from-red-500 to-red-600',
    },
    {
      label: 'Manual Approvals',
      value: manualApprovals,
      icon: Shield,
      color: 'from-violet-500 to-violet-600',
    },
    {
      label: "Today's Actions",
      value: todayActions,
      icon: Clock,
      color: 'from-emerald-500 to-emerald-600',
    },
  ];

  const handleExport = () => {
    const headers = [
      'Action ID',
      'Timestamp (UTC)',
      'Time (EAT)',
      'Performed By',
      'User Role',
      'Branch',
      'Customer MSISDN',
      'Contract ID',
      'Device ID',
      'Action Name',
      'Old Value',
      'New Value',
      'Reason',
      'Approval Reference',
      'Source Channel',
      'Result',
    ];
    const rows = filtered.map((l) => [
      l.actionId,
      toInstant(l.timestamp)?.toISOString() ?? l.timestamp,
      `"${formatDateTime(l.timestamp, '', true)}"`,
      l.performedBy,
      l.userRole,
      l.branch || '',
      l.customerMsisdn || '',
      l.contractId || '',
      l.deviceId || '',
      l.actionName,
      l.oldValue || '',
      l.newValue || '',
      l.reason,
      l.approvalReference || '',
      l.sourceChannel,
      l.result,
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveBlob(blob, `audit-logs-${businessToday()}.csv`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Audit Logs</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Complete audit trail for all financial, contract, device and user
            actions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={filtered.length === 0}
            className="gap-1.5"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => refetch()}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {isLoading ? '—' : s.value}
                </p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by ID, user, action, contract, phone or device..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[140px] h-9 text-xs">
              <Filter className="w-3 h-3 mr-1 text-muted-foreground" />
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[140px] h-9 text-xs">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              {actionOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="w-[140px] h-9 text-xs">
              <SelectValue placeholder="Channel" />
            </SelectTrigger>
            <SelectContent>
              {channelOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={resultFilter} onValueChange={setResultFilter}>
            <SelectTrigger className="w-[120px] h-9 text-xs">
              <SelectValue placeholder="Result" />
            </SelectTrigger>
            <SelectContent>
              {resultOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-primary" />
                Event Log
              </CardTitle>
              <CardDescription className="text-xs">
                {filtered.length} event{filtered.length !== 1 ? 's' : ''}{' '}
                {filtered.length !== allLogs.length && `of ${allLogs.length} total`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6">
              <ApiErrorCard
                error={error}
                onRetry={() => refetch()}
                title="Failed to load audit logs"
              />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No audit events found</p>
              <p className="text-sm">
                {allLogs.length > 0
                  ? 'Try adjusting your filters.'
                  : 'Audit events will appear here as actions are performed.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="w-8" />
                    <TableHead>
                      <span className="inline-flex items-center gap-1.5">
                        Timestamp
                        <TimeZoneHint />
                      </span>
                    </TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Performed By</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="hidden lg:table-cell">
                      Channel
                    </TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((log) => {
                    const isExpanded = expandedId === log.actionId;
                    return (
                      <>
                        <TableRow
                          key={log.actionId}
                          className="hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() =>
                            setExpandedId(isExpanded ? null : log.actionId)
                          }
                        >
                          <TableCell className="p-2">
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {formatDateTime(log.timestamp, '—', true)}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-sm">
                              {log.actionName}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm">
                            {log.performedBy}
                          </TableCell>
                          <TableCell>{roleBadge(log.userRole)}</TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {channelBadge(log.sourceChannel)}
                          </TableCell>
                          <TableCell>{resultBadge(log.result)}</TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${log.actionId}-detail`}>
                            <TableCell colSpan={7} className="p-3">
                              <AuditDetail entry={log} />
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
